import React, { useState, useRef, useEffect } from 'react';

import Button from '../styled/Button';
import { useTranslation } from 'react-i18next';
import { CollectionPropertyValue } from '../RealtimeCollectionProvider';

function CollectionItemPropertySelect({
    property,
    onChange,
    showEmptyState = false,
}: {
    property: CollectionPropertyValue;
    onChange: (value: string) => void;
    showEmptyState?: boolean;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(property.value ?? '');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hasValue = value.trim() !== '';
    const { t } = useTranslation();

    React.useEffect(() => {
        setValue(property.value ?? '');
    }, [property.value]);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            const textarea = textareaRef.current;
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [value, isEditing]);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            const textarea = textareaRef.current;
            setTimeout(() => {
                textarea.focus();
                // Set cursor position at the end
                const length = textarea.value.length;
                textarea.setSelectionRange(length, length);
            }, 0);
        }
    }, [isEditing]);

    if (isEditing) {
        return (
            <textarea
                ref={textareaRef}
                value={value}
                className="flex items-center text-sm bg-transparent focus:outline-none px-1 w-full resize-none overflow-hidden"
                rows={1}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => {
                    onChange(value);
                    setIsEditing(false);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        onChange(value);
                        setIsEditing(false);
                    } else if (e.key === 'Escape') {
                        setValue(property.value ?? '');
                        setIsEditing(false);
                    }
                }}
            />
        );
    }

    if (hasValue) {
        return (
            <div className="flex">
                <div
                    className="min-h-6 flex items-center leading-normal px-1 rounded cursor-pointer hover:bg-saga-gray-200 dark:hover:bg-zinc-200/10 break-words whitespace-normal"
                    onClick={() => setIsEditing(true)}
                    data-testid="property-input"
                >
                    <span className="text-sm">{value}</span>
                </div>
            </div>
        );
    }

    if (showEmptyState) {
        return (
            <Button.DashedPill onClick={() => setIsEditing(true)} data-testid="property-input">
                <div className="flex space-x-1 items-center h-5">
                    <span className="whitespace-nowrap">{t('collections.add_value')}</span>
                </div>
            </Button.DashedPill>
        );
    }

    return null;
}

export default CollectionItemPropertySelect;
